// Screen: "Analytics"
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getPlayerLeaderboard, getPlayerStats, getAILeaderboard,
  getAIPlayerDetailStats, getTrumpCallingAnalytics, getSiteStats
} from '../apiClient';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

function StatBar({ label, attempts, successes, isMobile }) {
  if (!attempts || attempts === 0) return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: isMobile ? 11 : 12, color: "#94a3b8", marginBottom: 4 }}>
        <span>{label}</span>
        <span style={{ color: "#4b5563" }}>No data</span>
      </div>
      <div style={{ height: 8, background: "#1e293b", borderRadius: 4 }} />
    </div>
  );
  const rate = ((successes / attempts) * 100).toFixed(1);
  const rateNum = parseFloat(rate);
  const barColor = rateNum >= 60 ? "#22c55e" : rateNum >= 40 ? "#f59e0b" : "#ef4444";
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: isMobile ? 11 : 12, color: "#c9d1d9", marginBottom: 4 }}>
        <span>{label}</span>
        <span style={{ color: barColor, fontWeight: 600 }}>{rate}% ({successes}/{attempts})</span>
      </div>
      <div style={{ height: 8, background: "#1e293b", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${Math.min(rateNum, 100)}%`, background: barColor, borderRadius: 4, transition: "width 0.3s" }} />
      </div>
    </div>
  );
}

function Section({ title, children, isMobile }) {
  return (
    <div style={{ background: "#161b22", border: "1px solid #30363d", borderRadius: 10, padding: isMobile ? "14px 16px" : "18px 22px", marginBottom: 20 }}>
      <h3 style={{ color: "#58a6ff", fontSize: isMobile ? 14 : 15, fontWeight: 700, marginBottom: 14 }}>{title}</h3>
      {children}
    </div>
  );
}

function BigStat({ value, label, color = "#58a6ff", isMobile }) {
  return (
    <div style={{ flex: 1, minWidth: isMobile ? 80 : 120, background: "#0d1117", borderRadius: 8, padding: isMobile ? "10px 8px" : "12px 16px", textAlign: "center" }}>
      <div style={{ fontSize: isMobile ? 20 : 28, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: isMobile ? 9 : 11, color: "#8b949e" }}>{label}</div>
    </div>
  );
}

const tabBtnStyle = (active, isMobile) => ({
  background: active ? '#238636' : 'transparent',
  color: active ? '#fff' : '#8b949e',
  border: active ? 'none' : '1px solid #30363d',
  borderRadius: 6,
  padding: isMobile ? '6px 10px' : '8px 16px',
  fontSize: isMobile ? 11 : 13,
  cursor: 'pointer',
  fontFamily: "'Georgia', serif"
});

const thStyle = (isMobile) => ({ textAlign: 'left', padding: isMobile ? '6px 4px' : '8px 8px', color: '#8b949e', fontWeight: 600, fontSize: isMobile ? 10 : 12, whiteSpace: 'nowrap' });
const tdStyle = (isMobile) => ({ padding: isMobile ? '6px 4px' : '8px 8px', fontSize: isMobile ? 11 : 13, color: '#c9d1d9', borderTop: '1px solid #21262d' });

export default function AnalyticsPage() {
  const { user, token, isClubMember } = useAuth();
  const isMobile = useIsMobile();
  const [tab, setTab] = useState('mystats');
  const [loading, setLoading] = useState(true);

  // Data states
  const [myStats, setMyStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [aiLeaderboard, setAiLeaderboard] = useState([]);
  const [expandedAI, setExpandedAI] = useState(null);
  const [aiDetail, setAiDetail] = useState(null);
  const [trumpAnalytics, setTrumpAnalytics] = useState(null);
  const [siteStats, setSiteStats] = useState(null);

  useEffect(() => {
    setLoading(true);
    if (tab === 'mystats' && user) {
      getPlayerStats(token, user.userId).then(data => { setMyStats(data); setLoading(false); });
    } else if (tab === 'leaderboard') {
      getPlayerLeaderboard(50).then(data => { setLeaderboard(data); setLoading(false); });
    } else if (tab === 'ai') {
      getAILeaderboard().then(data => { setAiLeaderboard(data); setLoading(false); });
    } else if (tab === 'trump') {
      Promise.all([getTrumpCallingAnalytics(), getSiteStats()]).then(([trump, site]) => {
        setTrumpAnalytics(trump);
        setSiteStats(site);
        setLoading(false);
      });
    } else if (tab === 'charts' && user) {
      getPlayerStats(token, user.userId).then(data => { setMyStats(data); setLoading(false); });
    } else {
      setLoading(false);
    }
  }, [tab, token, user]);

  const handleExpandAI = async (aiName) => {
    if (expandedAI === aiName) { setExpandedAI(null); return; }
    setExpandedAI(aiName);
    setAiDetail(null);
    const detail = await getAIPlayerDetailStats(aiName);
    setAiDetail(detail);
  };

  const suitSymbol = (suit) => {
    const map = { hearts: '\u2665', diamonds: '\u2666', clubs: '\u2663', spades: '\u2660' };
    return map[suit] || suit;
  };

  const suitColor = (suit) => {
    return (suit === 'hearts' || suit === 'diamonds') ? '#ef4444' : '#e2e8f0';
  };

  const th = thStyle(isMobile);
  const td = tdStyle(isMobile);

  return (
    <div style={{
      padding: isMobile ? '20px 16px 40px' : '40px 24px',
      maxWidth: 700, margin: '0 auto',
      fontFamily: "'Georgia', serif", color: '#e2e8f0'
    }}>
      <h1 style={{ fontSize: isMobile ? 24 : 36, fontWeight: 700, marginBottom: isMobile ? 16 : 20 }}>Analytics</h1>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: isMobile ? 6 : 8, marginBottom: isMobile ? 16 : 24, flexWrap: 'wrap' }}>
        <button style={tabBtnStyle(tab === 'mystats', isMobile)} onClick={() => setTab('mystats')}>My Stats</button>
        <button style={tabBtnStyle(tab === 'leaderboard', isMobile)} onClick={() => setTab('leaderboard')}>Leaderboard</button>
        {isClubMember ? (
          <>
            <button style={tabBtnStyle(tab === 'ai', isMobile)} onClick={() => setTab('ai')}>AI Players</button>
            <button style={tabBtnStyle(tab === 'trump', isMobile)} onClick={() => setTab('trump')}>Trump Analytics</button>
            <button style={tabBtnStyle(tab === 'charts', isMobile)} onClick={() => setTab('charts')}>Charts</button>
          </>
        ) : (
          <button onClick={() => {}} style={{ ...tabBtnStyle(false, isMobile), opacity: 0.5, cursor: 'default', position: 'relative' }} title="Club Members only">
            + 3 more tabs (Club)
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#8b949e' }}>Loading...</div>
      ) : (
        <>
          {/* ============ MY STATS TAB ============ */}
          {tab === 'mystats' && (
            !myStats ? (
              <Section title="My Stats" isMobile={isMobile}>
                <p style={{ color: '#8b949e', fontSize: isMobile ? 13 : 14 }}>No game data found. Play some games to see your stats!</p>
              </Section>
            ) : (
              <>
                <Section title="Game Summary" isMobile={isMobile}>
                  <div style={{ display: "flex", gap: isMobile ? 8 : 16, flexWrap: "wrap" }}>
                    <BigStat value={myStats.gameStats?.gamesPlayed || 0} label="Games Played" isMobile={isMobile} />
                    <BigStat value={myStats.gameStats?.wins || 0} label="Wins" color="#22c55e" isMobile={isMobile} />
                    <BigStat value={myStats.gameStats?.winRate != null ? `${myStats.gameStats.winRate}%` : '—'} label="Win Rate" color="#f59e0b" isMobile={isMobile} />
                  </div>
                  {myStats.bucksStats && (
                    <div style={{ display: "flex", gap: isMobile ? 8 : 16, marginTop: 12 }}>
                      <BigStat value={myStats.bucksStats.totalWon?.toLocaleString() || 0} label="Most Bucks Won" color="#22c55e" isMobile={isMobile} />
                      <BigStat value={myStats.bucksStats.totalLost?.toLocaleString() || 0} label="Most Bucks Lost" color="#ef4444" isMobile={isMobile} />
                      <BigStat value={`${myStats.bucksStats.netProfit >= 0 ? '+' : ''}${myStats.bucksStats.netProfit?.toLocaleString() || 0}`} label="Net Profit" color={myStats.bucksStats.netProfit >= 0 ? '#3fb950' : '#f85149'} isMobile={isMobile} />
                    </div>
                  )}
                </Section>

                {myStats.trumpStats && (
                  <Section title="Trump Calling" isMobile={isMobile}>
                    <StatBar label="Overall Success" attempts={myStats.trumpStats.totalCalls} successes={myStats.trumpStats.successfulCalls} isMobile={isMobile} />
                    {myStats.trumpStats.avgTrumpCount != null && (
                      <div style={{ fontSize: isMobile ? 11 : 12, color: '#8b949e', marginTop: 4 }}>
                        Avg trump in hand when calling: <span style={{ color: '#58a6ff', fontWeight: 600 }}>{myStats.trumpStats.avgTrumpCount}</span>
                        {myStats.trumpStats.withRightBower > 0 && <> | With right bower: <span style={{ color: '#22c55e' }}>{myStats.trumpStats.withRightBower}x</span></>}
                        {myStats.trumpStats.withLeftBower > 0 && <> | With left bower: <span style={{ color: '#f59e0b' }}>{myStats.trumpStats.withLeftBower}x</span></>}
                      </div>
                    )}
                  </Section>
                )}

                {myStats.playStats && Object.keys(myStats.playStats).length > 0 && (
                  <Section title="Play/Sit Decisions" isMobile={isMobile}>
                    {myStats.playStats.play && (
                      <div style={{ fontSize: isMobile ? 12 : 13, color: '#c9d1d9', marginBottom: 8 }}>
                        Played: <span style={{ color: '#22c55e', fontWeight: 600 }}>{myStats.playStats.play.count}x</span>
                        {myStats.playStats.play.avgTricks != null && <> (avg {myStats.playStats.play.avgTricks} tricks)</>}
                      </div>
                    )}
                    {myStats.playStats.sit && (
                      <div style={{ fontSize: isMobile ? 12 : 13, color: '#c9d1d9' }}>
                        Sat out: <span style={{ color: '#f59e0b', fontWeight: 600 }}>{myStats.playStats.sit.count}x</span>
                      </div>
                    )}
                  </Section>
                )}
              </>
            )
          )}

          {/* ============ LEADERBOARD TAB ============ */}
          {tab === 'leaderboard' && (
            <Section title="Player Leaderboard" isMobile={isMobile}>
              {leaderboard.length === 0 ? (
                <p style={{ color: '#8b949e', fontSize: isMobile ? 13 : 14 }}>No player data yet.</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={th}>#</th>
                        <th style={th}>Player</th>
                        <th style={th}>Games</th>
                        <th style={th}>Wins</th>
                        <th style={th}>Win %</th>
                        {!isMobile && <th style={th}>Avg Score</th>}
                        {!isMobile && <th style={th}>Bucks</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboard.map((p, i) => (
                        <tr key={i} style={user?.userId === p.userId ? { background: '#1c2333' } : {}}>
                          <td style={td}>{i + 1}</td>
                          <td style={{ ...td, fontWeight: user?.userId === p.userId ? 700 : 400, color: user?.userId === p.userId ? '#58a6ff' : '#c9d1d9' }}>
                            {p.nickname || 'Anonymous'}
                          </td>
                          <td style={td}>{p.gamesPlayed}</td>
                          <td style={td}>{p.wins}</td>
                          <td style={{ ...td, color: p.winRate >= 50 ? '#22c55e' : '#f59e0b' }}>{p.winRate}%</td>
                          {!isMobile && <td style={td}>{p.avgScore}</td>}
                          {!isMobile && <td style={{ ...td, color: '#fbbf24' }}>{p.balance?.toLocaleString()}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
          )}

          {/* ============ AI PLAYERS TAB ============ */}
          {tab === 'ai' && (
            <Section title="AI Player Rankings" isMobile={isMobile}>
              {aiLeaderboard.length === 0 ? (
                <p style={{ color: '#8b949e', fontSize: isMobile ? 13 : 14 }}>No AI player data yet.</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={th}>#</th>
                        <th style={th}>AI Name</th>
                        <th style={th}>Games</th>
                        <th style={th}>Wins</th>
                        <th style={th}>Win %</th>
                        {!isMobile && <th style={th}>Avg Score</th>}
                        {!isMobile && <th style={th}>Bucks</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {aiLeaderboard.map((ai, i) => (
                        <React.Fragment key={ai.aiName}>
                          <tr
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleExpandAI(ai.aiName)}
                          >
                            <td style={td}>{i + 1}</td>
                            <td style={{ ...td, color: '#a78bfa', fontWeight: 600 }}>{ai.aiName}</td>
                            <td style={td}>{ai.gamesPlayed}</td>
                            <td style={td}>{ai.wins}</td>
                            <td style={{ ...td, color: ai.winRate >= 50 ? '#22c55e' : '#f59e0b' }}>{ai.winRate}%</td>
                            {!isMobile && <td style={td}>{ai.avgScore}</td>}
                            {!isMobile && <td style={{ ...td, color: '#fbbf24' }}>{ai.balance?.toLocaleString()}</td>}
                          </tr>
                          {expandedAI === ai.aiName && (
                            <tr>
                              <td colSpan={isMobile ? 5 : 7} style={{ padding: isMobile ? '10px 8px' : '12px 16px', background: '#0d1117', borderTop: '1px solid #21262d' }}>
                                {!aiDetail ? (
                                  <span style={{ color: '#8b949e', fontSize: 12 }}>Loading...</span>
                                ) : (
                                  <div style={{ display: 'flex', gap: isMobile ? 12 : 24, flexWrap: 'wrap' }}>
                                    <div style={{ flex: 1, minWidth: isMobile ? 140 : 200 }}>
                                      <div style={{ fontSize: isMobile ? 11 : 12, color: '#58a6ff', fontWeight: 600, marginBottom: 8 }}>Trump Calling</div>
                                      <StatBar label="Success Rate" attempts={aiDetail.trumpStats?.totalCalls} successes={aiDetail.trumpStats?.successfulCalls} isMobile={isMobile} />
                                      {aiDetail.trumpStats?.avgTrumpCount != null && (
                                        <div style={{ fontSize: isMobile ? 10 : 11, color: '#8b949e' }}>
                                          Avg trump: {aiDetail.trumpStats.avgTrumpCount} | R-bower: {aiDetail.trumpStats.withRightBower}x | L-bower: {aiDetail.trumpStats.withLeftBower}x
                                        </div>
                                      )}
                                    </div>
                                    <div style={{ flex: 1, minWidth: isMobile ? 140 : 200 }}>
                                      <div style={{ fontSize: isMobile ? 11 : 12, color: '#58a6ff', fontWeight: 600, marginBottom: 8 }}>Play/Sit</div>
                                      {aiDetail.playStats?.play && (
                                        <div style={{ fontSize: isMobile ? 11 : 12, color: '#c9d1d9', marginBottom: 4 }}>
                                          Played: {aiDetail.playStats.play.count}x (avg {aiDetail.playStats.play.avgTricks} tricks)
                                        </div>
                                      )}
                                      {aiDetail.playStats?.sit && (
                                        <div style={{ fontSize: isMobile ? 11 : 12, color: '#c9d1d9' }}>
                                          Sat out: {aiDetail.playStats.sit.count}x
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ fontSize: 10, color: '#4b5563', marginTop: 8 }}>Click a row to expand details</div>
                </div>
              )}
            </Section>
          )}

          {/* ============ TRUMP ANALYTICS TAB ============ */}
          {tab === 'trump' && (
            <>
              {siteStats && (
                <Section title="Site Overview" isMobile={isMobile}>
                  <div style={{ display: "flex", gap: isMobile ? 8 : 16, flexWrap: "wrap" }}>
                    <BigStat value={siteStats.totalGames} label="Games Completed" isMobile={isMobile} />
                    <BigStat value={siteStats.totalUsers} label="Players" color="#22c55e" isMobile={isMobile} />
                    <BigStat value={siteStats.totalHands} label="Hands Dealt" color="#a78bfa" isMobile={isMobile} />
                    <BigStat value={siteStats.totalTricks} label="Tricks Played" color="#f59e0b" isMobile={isMobile} />
                  </div>
                  {siteStats.mostPopularSuit && (
                    <div style={{ marginTop: 12, fontSize: isMobile ? 12 : 13, color: '#8b949e', textAlign: 'center' }}>
                      Most popular trump: <span style={{ color: suitColor(siteStats.mostPopularSuit), fontWeight: 700, fontSize: isMobile ? 14 : 16 }}>
                        {suitSymbol(siteStats.mostPopularSuit)}
                      </span> {siteStats.mostPopularSuit}
                    </div>
                  )}
                </Section>
              )}

              {trumpAnalytics && (
                <>
                  <Section title="Success Rate by Trump Count" isMobile={isMobile}>
                    {trumpAnalytics.byTrumpCount?.map(row => (
                      <StatBar
                        key={row.trumpCount}
                        label={`${row.trumpCount} trump in hand`}
                        attempts={row.attempts}
                        successes={row.successes}
                        isMobile={isMobile}
                      />
                    ))}
                  </Section>

                  <Section title="Bower Impact on Trump Calling" isMobile={isMobile}>
                    {trumpAnalytics.byBower?.map((row, i) => {
                      let label = 'No bowers';
                      if (row.hadRightBower && row.hadLeftBower) label = 'Both bowers';
                      else if (row.hadRightBower) label = 'Right bower only';
                      else if (row.hadLeftBower) label = 'Left bower only';
                      return <StatBar key={i} label={label} attempts={row.attempts} successes={row.successes} isMobile={isMobile} />;
                    })}
                  </Section>
                </>
              )}

              {!trumpAnalytics && (
                <Section title="Trump Analytics" isMobile={isMobile}>
                  <p style={{ color: '#8b949e', fontSize: isMobile ? 13 : 14 }}>No trump calling data yet.</p>
                </Section>
              )}
            </>
          )}

          {/* ============ CHARTS TAB ============ */}
          {tab === 'charts' && (
            myStats ? (
              <>
                {/* Trump Call Success by Suit */}
                {myStats.trumpStats && myStats.trumpStats.length > 0 && (
                  <Section title="Trump Call Success by Suit" isMobile={isMobile}>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={myStats.trumpStats.map(s => ({
                        name: s.suit ? s.suit.charAt(0).toUpperCase() + s.suit.slice(1) : 'Unknown',
                        success: parseFloat(((s.successes / Math.max(s.attempts, 1)) * 100).toFixed(1)),
                        attempts: s.attempts
                      }))}>
                        <XAxis dataKey="name" stroke="#8b949e" fontSize={11} />
                        <YAxis stroke="#8b949e" fontSize={11} domain={[0, 100]} />
                        <Tooltip contentStyle={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 8, color: '#e2e8f0', fontSize: 12 }} />
                        <Bar dataKey="success" fill="#58a6ff" name="Success %" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Section>
                )}

                {/* Play vs Sit Decisions */}
                {myStats.gameStats && (
                  <Section title="Play vs Sit Decisions" isMobile={isMobile}>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <PieChart width={200} height={200}>
                        <Pie
                          data={[
                            { name: 'Played', value: myStats.gameStats.handsPlayed || 1 },
                            { name: 'Sat', value: Math.max((myStats.gameStats.totalHands || 0) - (myStats.gameStats.handsPlayed || 0), 0) || 1 }
                          ]}
                          cx={100} cy={100}
                          innerRadius={50} outerRadius={80}
                          dataKey="value"
                        >
                          <Cell fill="#3fb950" />
                          <Cell fill="#8b949e" />
                        </Pie>
                        <Tooltip contentStyle={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 8, color: '#e2e8f0', fontSize: 12 }} />
                      </PieChart>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 8 }}>
                      <span style={{ fontSize: 12, color: '#3fb950' }}>&#9632; Played</span>
                      <span style={{ fontSize: 12, color: '#8b949e' }}>&#9632; Sat</span>
                    </div>
                  </Section>
                )}

                {/* Game Performance */}
                <Section title="Performance Summary" isMobile={isMobile}>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <BigStat value={myStats.gameStats?.gamesPlayed || 0} label="Total Games" isMobile={isMobile} />
                    <BigStat value={myStats.gameStats?.wins || 0} label="Wins" color="#3fb950" isMobile={isMobile} />
                    <BigStat
                      value={myStats.gameStats?.gamesPlayed ? `${Math.round((myStats.gameStats.wins / myStats.gameStats.gamesPlayed) * 100)}%` : '0%'}
                      label="Win Rate" color="#58a6ff" isMobile={isMobile}
                    />
                    <BigStat value={myStats.gameStats?.longestStreak || 0} label="Best Streak" color="#f59e0b" isMobile={isMobile} />
                  </div>
                </Section>
              </>
            ) : (
              <Section title="Charts" isMobile={isMobile}>
                <p style={{ color: '#8b949e', fontSize: 14 }}>Play some games to see your charts!</p>
              </Section>
            )
          )}
        </>
      )}
    </div>
  );
}
